<template>
  <div
    class="modern-tree-table"
    :class="{
      'modern-tree-table--fill': fillAvailable,
      'modern-tree-table--fixed-scroll': Boolean(fixedScrollHeight),
      'modern-tree-table--row-context-actions': showRowContextActions,
      'modern-tree-table--row-context-overflow': showRowContextActions && rowContextVisibility.showOverflow,
      'modern-tree-table--hide-border': hideBorder,
      'modern-tree-table--skip-default-styles': skipDefaultStyles,
      [`modern-tree-table--style-${tableStyleClass}`]: true,
      [tableWidthScopeClass]: true,
    }"
    :style="fixedScrollStyle"
    ref="tableContainer"
    tabindex="-1"
    @dragenter.capture="onTableDragEnterCapture"
    @dragover.capture="onTableDragOverCapture"
    @drop.capture="onTableDropCapture">
    <component v-if="columnWidthsCss" :is="'style'">{{ columnWidthsCss }}</component>
    <div v-if="showControlPanel" class="table-chrome-bar table-chrome-bar--summary">
      <MPagination
        v-model="localMeta"
        variant="summary"
        :element-id="undefined"
        :rows-per-page-options="parsedMaxOnPageValues"
        class="table-pagination top"
        @change="onPaginationChange"
      />
    </div>
    <!-- Custom Context Menu -->
    <ContextMenu ref="contextMenu" :model="contextMenuModel" @hide="onContextMenuHide" />
    <TreeTable :value="nodes" v-model:selectionKeys="selectedKeys" v-model:expandedKeys="expandedKeys"
      :selection-mode="props.withCheckbox ? 'checkbox' : undefined"
      :class="{ 'tree-table-without-header': props.showTableHeader === false }"
      :loading="loading" @node-expand="onNodeExpand" @node-select="onNodeSelect" @contextmenu="onTreeTableContextMenu">
      <Column expander style="width: 3rem" />

      <Column v-for="(column, index) in headers" :key="index" :field="column.value" :header="column.name"
        :pt="getColumnBodyCellPt(column)">
        <template #body="slotProps">
          <div class="tree-table-cell clickable"
            :class="{
              'row-clicked': treeRowKeysEqual(clickedRowKey, slotProps.node.key),
              'row-search-match': treeRowKeysEqual(searchHighlightKey, slotProps.node.key),
            }"
            :data-row-key="normalizeTreeRowKey(slotProps.node.key)"
            :data-hide-checkbox="nodeHidesCheckbox(slotProps.node) ? 'true' : undefined"
            :data-not-selective="!isNodeSelective(slotProps.node) ? 'true' : undefined"
            :style="slotProps.node.rowPresentation?.rowInlineStyle"
            :title="slotProps.node.rowPresentation?.rowTitle || undefined"
            :draggable="canDrag"
            @dragstart="onCellDragStart($event, slotProps.node)"
            @dragend="onCellDragEnd"
            @contextmenu.stop.prevent="onContextMenu(slotProps.node, column.value, $event)"
            @click="onCellClick(slotProps.node, column.value, $event)"
            @dblclick="onCellDoubleClick(slotProps.node, column.value)">
            <TableCellRenderer :item="slotProps.node.data" :column="column"
              :table-field-name="tableName"
              :row-presentation="slotProps.node.rowPresentation"
              :leading-tree-icon="index === 0 ? slotProps.node.icon : undefined"
              :rows-expanded="isMultiLineTextColumn(column)"
              @action="(event) => emit('action', event)" />
          </div>
        </template>
        <template #header="slotProps">
          <div class="header-content" :class="{ 'inline-actions-header': column.isInlineAction }">
            <span v-if="!column.isInlineAction" class="header-text">{{ slotProps.header }}</span>
          </div>
        </template>
      </Column>

      <Column
        v-if="showRowContextActions"
        header-class="table-row-context-actions-col"
        body-class="table-row-context-actions-col"
        :header-style="rowContextActionsColumnStyle"
        :body-style="rowContextActionsColumnStyle"
      >
        <template #body="slotProps">
          <div
            class="tree-table-row-context-hit"
            @click="onRowContextCellClick($event, slotProps.node)"
            @dblclick="onRowContextCellDblclick($event, slotProps.node)"
          >
            <TableRowContextActions
              :key="String(slotProps.node?.key ?? '')"
              v-if="rowContextShowPrimary(slotProps.node)"
              :primary-label="rowContextPrimaryPresentation(slotProps.node).label"
              :primary-icon="rowContextPrimaryPresentation(slotProps.node).icon"
              :show-overflow="rowContextShowOverflow(slotProps.node)"
              :overflow-label="t('ui.moreActions')"
              @shown="onRowContextShown(slotProps.node)"
              @primary="onRowContextPrimary($event, slotProps.node)"
              @overflow="onRowContextOverflow($event, slotProps.node)"
            />
          </div>
        </template>
      </Column>

      <template #empty>
        <div class="empty-state">
          <i class="empty-icon pi pi-table" />
          <p class="empty-text">Нет данных для отображения</p>
        </div>
      </template>
    </TreeTable>
    <MPagination
      v-if="showControlPanel"
      v-model="localMeta"
      variant="footer"
      :rows-per-page-options="parsedMaxOnPageValues"
      class="table-pagination bottom"
      @change="onPaginationChange"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch, onBeforeUnmount } from 'vue'
import TreeTable from 'primevue/treetable'
import Column from 'primevue/column'
import ContextMenu from 'primevue/contextmenu'
import TableCellRenderer from './TableCellRenderer.vue'
import TableRowContextActions from './TableRowContextActions.vue'
import MPagination from '@/components/ui/MPagination.vue'
import { parseTableColumnWidth } from '@/common/utils/parseTableColumnWidth'
import { resolveSwingTableColumnWidths } from '@/common/utils/resolveSwingTableColumnWidths'
import { PLATFORM_TABLE_FILL_MIN_BODY_PX } from '@/common/utils/resolveTableScrollHeight'
import { canonicalTreeNodeId, TREE_TABLE_NODE_KEY_FIELD } from '@/common/utils/treeTableIds'
import {
  buildTreeTableRowSwingPresentation,
  type TreeTableRowSwingPresentation,
} from '@/common/utils/treeTableRowSwingPresentation'
import type { FlatVisibleTreeRow } from '@/common/utils/treeTableReloadNodePayload'
import { useTableContextMenu } from '@/common/composables/useTableContextMenu'
import { useI18n } from '@/common/composables/useI18n'
import {
  filterPlatformContextMenuItems,
  resolveTableRowContextActionsColumnWidthPx,
  resolveTableRowContextActionsVisibility,
  resolveTableRowContextOverride,
  resolveTableRowPrimaryActionPresentation,
  tableRowContextOverridesEqual,
  createTableRowContextPreviewGate,
} from '@/common/utils/tableRowContextActions'
import { IKey } from '@/common/constants/ikey'
import {
  isTreeTableCheckBoxVisible,
  isTreeTableSelectiveNode,
} from '@/common/utils/treeTableSelectiveNode'
import {
  applyTreeCheckingModeSelection,
  normalizeTreeCheckingMode,
} from '@/common/utils/treeTableCheckingMode'
import { isTreeTableDropTarget } from '@/common/utils/treeTableDropTarget'
import {
  isTreeTableGrowUpRootNode,
  TREE_TABLE_GROW_UP_ROOT_KEY,
} from '@/common/utils/treeTableFindParent'

interface TreeNode {
  key: string
  label: string
  data: any
  children?: TreeNode[]
  icon?: string
  rowPresentation?: TreeTableRowSwingPresentation
}

interface Props {
  items?: any[]
  headers?: any[]
  value?: any
  itemValue?: string
  expandByDefault?: boolean
  showRootNode?: boolean
  checkingMode?: string
  loading?: boolean
  contextMenuItems?: any[]
  withCheckbox?: boolean
  showTableHeader?: boolean
  /** Имя поля TABLE на форме — для listbox code→description в ячейках */
  tableName?: string
  /** Паритет Swing TreeTable isDraggable + event onDrop */
  dragEnabled?: boolean
  readonly?: boolean
  disabled?: boolean
  fillAvailable?: boolean
  /** Same resolver as MTable: flex leftover or CSS body height (compact 2730). */
  scrollHeight?: string | null
  /**
   * Async-хук до show контекстного меню (onInlineMenuShow / visibility).
   * Если задан — вызывается вместо/до emit; Vue emit не ждёт parent listener.
   */
  beforeContextMenuShow?: (event: {
    node: TreeNode
    target: Event
    column: string
    selectedIdx: number
    preview?: boolean
    data?: any
  }) => void | any[] | Promise<void | any[]>
  previewInlineMenu?: boolean
  forbidSelectNode?: boolean
  hideCheckBoxOnAllLevel?: boolean
  hideCheckBoxLevel?: string
  isMultiSelectEnabled?: boolean
  descRoot?: string
  hideBorder?: boolean
  tableStyle?: string
  showControlPanel?: boolean
  maxOnPageValues?: string
  showFormStateOptions?: boolean
  savedExpandedKeys?: Record<string, boolean> | null
  meta?: { page?: number; pages?: number; itemsPerPage?: number; total?: number }
  skipDefaultStyles?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  headers: () => [],
  value: () => ({
    count: 10,
    page: 1,
    pages: 1
  }),
  itemValue: 'value',
  expandByDefault: false,
  showRootNode: false,
  checkingMode: 'PROPAGATE_PRESERVING_UNCHECK',
  loading: false,
  contextMenuItems: () => [],
  withCheckbox: true,
  showTableHeader: true,
  tableName: '',
  dragEnabled: false,
  readonly: false,
  disabled: false,
  fillAvailable: false,
  scrollHeight: null,
  previewInlineMenu: false,
  forbidSelectNode: false,
  hideCheckBoxOnAllLevel: false,
  hideCheckBoxLevel: '',
  isMultiSelectEnabled: false,
  descRoot: '',
  hideBorder: false,
  tableStyle: '',
  showControlPanel: false,
  maxOnPageValues: '',
  showFormStateOptions: false,
  savedExpandedKeys: null,
  meta: () => ({ page: 1, pages: 1, itemsPerPage: 50 }),
  skipDefaultStyles: false,
})
// computed-алиасы для безопасного использования в шаблоне
const loading = computed(() => props.loading)
const headers = computed(() => props.headers)

const { t } = useI18n()

const hideBorder = computed(() => !!props.hideBorder)
const tableStyleClass = computed(() => {
  const s = String(props.tableStyle ?? '').trim().replace(/\s+/g, '-')
  return s || 'standard'
})
const parsedMaxOnPageValues = computed(() => {
  const raw = String(props.maxOnPageValues ?? '').trim()
  if (!raw) return undefined
  const nums = raw
    .split(';')
    .map((p) => Number.parseInt(p.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0)
  return nums.length ? nums : undefined
})
const localMeta = ref({
  page: props.meta?.page ?? 1,
  pages: props.meta?.pages ?? 1,
  itemsPerPage: props.meta?.itemsPerPage ?? 50,
})
watch(
  () => props.meta,
  (m) => {
    if (!m) return
    localMeta.value = {
      page: m.page ?? localMeta.value.page,
      pages: m.pages ?? localMeta.value.pages,
      itemsPerPage: m.itemsPerPage ?? localMeta.value.itemsPerPage,
    }
  },
  { deep: true },
)
const onPaginationChange = (meta: { page: number; pages: number; itemsPerPage?: number }) => {
  localMeta.value = {
    page: meta.page,
    pages: meta.pages,
    itemsPerPage: meta.itemsPerPage ?? localMeta.value.itemsPerPage,
  }
  emit('update:meta', localMeta.value)
}

const selectiveFlags = computed(() => ({
  forbidSelectNode: !!props.forbidSelectNode,
  hideCheckBoxOnAllLevel: !!props.hideCheckBoxOnAllLevel,
  hideCheckBoxLevel: props.hideCheckBoxLevel ?? '',
}))

const isNodeSelective = (node: TreeNode): boolean => {
  if (isTreeTableGrowUpRootNode(node)) return false
  return isTreeTableSelectiveNode(node, selectiveFlags.value)
}

const nodeHidesCheckbox = (node: TreeNode): boolean => {
  const depth = Number((node as { treeDepth?: number }).treeDepth ?? 0)
  return !isTreeTableCheckBoxVisible(node, depth, selectiveFlags.value)
}

const platformContextMenuItems = computed(() =>
  filterPlatformContextMenuItems(props.contextMenuItems),
)
const rowContextVisibility = computed(() =>
  resolveTableRowContextActionsVisibility(platformContextMenuItems.value),
)
const rowActionsColumnPinned = ref(false)
const rowContextOverrideByKey = ref<
  Record<string, { showPrimary: boolean; showOverflow: boolean; icon?: string; label?: string }>
>({})
watch(
  () => rowContextVisibility.value.showColumn,
  (show) => {
    if (show) rowActionsColumnPinned.value = true
  },
  { immediate: true },
)

const showRowContextActions = computed(
  () =>
    rowActionsColumnPinned.value ||
    rowContextVisibility.value.showColumn ||
    Object.values(rowContextOverrideByKey.value).some((o) => o.showPrimary),
)
const rowContextActionsColumnWidthPx = computed(() => {
  if (!showRowContextActions.value) return 0
  return resolveTableRowContextActionsColumnWidthPx(rowContextVisibility.value.showOverflow)
})
const rowContextActionsColumnStyle = computed(() => ({
  width: `${rowContextActionsColumnWidthPx.value}px`,
  minWidth: `${rowContextActionsColumnWidthPx.value}px`,
  maxWidth: `${rowContextActionsColumnWidthPx.value}px`,
}))

const getColumnBodyCellPt = (column: { type?: string }) =>
  column.type ? { bodyCell: { 'data-type': column.type } } : undefined

const isMultiLineTextColumn = (column: { type?: string } | undefined): boolean =>
  String(column?.type ?? '').trim().toUpperCase() === 'MULTILINETEXT'

const EXPANDER_COLUMN_WIDTH_PX = 48
const tableWidthPx = ref(0)
let tableResizeObserver: ResizeObserver | null = null

const tableWidthScopeClass = computed(() => {
  const raw = String(props.tableName || 'default').replace(/[^a-zA-Z0-9_-]/g, '_')
  return `mtree-w-${raw}`
})

const fixedScrollHeight = computed(() => {
  const value = String(props.scrollHeight ?? '').trim()
  if (!value || value === 'flex' || props.fillAvailable) return null
  return value
})
const fixedScrollStyle = computed(() => {
  const style: Record<string, string> = {}
  if (fixedScrollHeight.value) style.maxHeight = fixedScrollHeight.value
  if (props.fillAvailable) {
    style['--platform-table-fill-min-body'] = `${PLATFORM_TABLE_FILL_MIN_BODY_PX}px`
  }
  return Object.keys(style).length > 0 ? style : undefined
})

const measureTableWidth = () => {
  const el = tableContainer.value
  if (!el) return
  const width = el.getBoundingClientRect().width
  if (width > 0) {
    tableWidthPx.value = Math.round(width)
  }
}

const resolvedColumnWidths = computed(() => {
  const width = tableWidthPx.value
  if (width <= 0 || !headers.value.length) return {}
  return resolveSwingTableColumnWidths(
    headers.value.map((column: { value?: string; width?: string; constantColumnWidth?: boolean; type?: string }) => ({
      value: String(column.value ?? ''),
      width: column.width,
      constantColumnWidth: column.constantColumnWidth,
      type: column.type,
    })),
    Math.max(0, width - EXPANDER_COLUMN_WIDTH_PX - rowContextActionsColumnWidthPx.value),
  )
})

const columnWidthsCss = computed(() => {
  const widths = resolvedColumnWidths.value
  const cols = headers.value
  if (!cols.length) return ''
  const scope = `.${tableWidthScopeClass.value}`
  let css = `${scope} .p-treetable-table {
  width: 100% !important;
  table-layout: fixed;
}
${scope} .p-treetable-table > thead > tr > th:nth-child(1),
${scope} .p-treetable-table > tbody > tr > td:nth-child(1) {
  width: ${EXPANDER_COLUMN_WIDTH_PX}px !important;
  max-width: ${EXPANDER_COLUMN_WIDTH_PX}px !important;
  min-width: ${EXPANDER_COLUMN_WIDTH_PX}px !important;
  box-sizing: border-box;
}
`
  cols.forEach((column: { value?: string; width?: string; constantColumnWidth?: boolean }, index: number) => {
    const px = widths[String(column.value ?? '')]
    if (px == null || px <= 0) return
    const nth = index + 2
    const spec = parseTableColumnWidth(column.width, column.constantColumnWidth)
    const isFixedWidth = spec.isFixed || column.constantColumnWidth
    const minWidthRule = isFixedWidth
      ? `min-width: ${px}px !important;`
      : 'min-width: 0 !important;'
    css += `${scope} .p-treetable-table > thead > tr > th:nth-child(${nth}),
${scope} .p-treetable-table > tbody > tr > td:nth-child(${nth}) {
  width: ${px}px !important;
  max-width: ${px}px !important;
  ${minWidthRule}
  overflow: hidden;
  box-sizing: border-box;
}
`
  })
  if (showRowContextActions.value) {
    const px = rowContextActionsColumnWidthPx.value
    css += `${scope} .p-treetable-table > thead > tr > th.table-row-context-actions-col,
${scope} .p-treetable-table > tbody > tr > td.table-row-context-actions-col {
  width: ${px}px !important;
  max-width: ${px}px !important;
  min-width: ${px}px !important;
  box-sizing: border-box;
}
`
  }
  return css
})

const canDrag = computed(
  () => !!props.dragEnabled && !props.readonly && !props.disabled && !props.loading,
)

/** MIME + fallback key between dragstart/drop (some browsers restrict getData until drop). */
const DND_KEY_MIME = 'application/x-mtree-table-key'

/** Память об источнике на время DnD (getData('text/plain') иногда пуст на dragover). */
const activeDragSourceKey = ref<string | null>(null)
/** Есть ли перетаскивание, начатое в этой таблице (для dragover по tr/td вне ячейки-колонки). */
const internalTreeDragActive = ref(false)

// defineEmits должно быть объявлено до watchers, чтобы его можно было вызвать
const emit = defineEmits<{
  'update:value': [value: any]
  'update:selected': [selected: any[]]
  'contextmenu': [event: { node: TreeNode; target: Event; column: string; selectedIdx: number }]
  'click': [event: { node: TreeNode; column: string; data: any; selectedIdx: number }]
  'dblclick': [event: { node: TreeNode; column: string; data: any; selectedIdx: number }]
  'load-children': [payload: { node: TreeNode; rowNum: number; expandedSnapshot: Record<string, boolean> }]
  'find-parent': [payload: { node: TreeNode }]
  'node-drop': [payload: Record<string, unknown>]
  'action': [event: any]
  'update:meta': [meta: { page: number; pages: number; itemsPerPage: number }]
  saveSetting: [event: { paramName: 'expandedKeys'; paramValue: Record<string, boolean> }]
}>()

// reactive state
const nodes = ref<TreeNode[]>([])
const selectedKeys = ref<Record<string, boolean>>({})
const expandedKeys = ref<Record<string, boolean>>({})
let lastRenderedItemsSignature = ''
/** После lazy load-children: ключ узла, который нужно оставить раскрытым, когда пришли дети (обход гонки с v-model expandedKeys). */
const pendingLazyExpandKey = ref<string | null>(null)
const clickedRowKey = ref<string | null>(null)
const searchHighlightKey = ref<string | null>(null)
let searchHighlightTimer: ReturnType<typeof setTimeout> | null = null
const tableContainer = ref<HTMLElement | null>(null)

onMounted(() => {
  measureTableWidth()
  nextTick(measureTableWidth)
  if (typeof ResizeObserver !== 'undefined' && tableContainer.value) {
    tableResizeObserver = new ResizeObserver(() => {
      measureTableWidth()
    })
    tableResizeObserver.observe(tableContainer.value)
  }
})

// Use shared context menu composable
const contextMenuItemsAfterShow = ref<any[] | null>(null)

const {
  contextMenu,
  contextMenuModel,
  handleContextMenu: handleTableContextMenu,
  prepareContextMenu,
  showPreparedContextMenu,
  onContextMenuHide,
} = useTableContextMenu({
  contextMenuItems: computed(() => props.contextMenuItems || []),
  getContextMenuItems: () => contextMenuItemsAfterShow.value ?? props.contextMenuItems ?? [],
  headers: computed(() => props.headers || []),
  onEmit: async (event) => {
    if (event.preview !== true) {
      contextMenuItemsAfterShow.value = null
    }
    const eventData = event?.data
    const allNodes = flattenNodes(nodes.value)
    const visibleNodes = flattenVisibleNodes(nodes.value)
    const selectedIdx =
      typeof event?.selectedIdx === 'number' && event.selectedIdx >= 0 ? event.selectedIdx : -1
    const eventTreeKey =
      eventData && typeof eventData === 'object'
        ? String((eventData as Record<string, unknown>)[TREE_TABLE_NODE_KEY_FIELD] ?? '').trim()
        : ''
    const eventId =
      eventData && typeof eventData === 'object'
        ? String(
            (eventData as Record<string, any>)?._id ??
              (eventData as Record<string, any>)?.fields?._id?.value ??
              '',
          ).trim()
        : ''
    const eventTypeId =
      eventData && typeof eventData === 'object'
        ? String(
            (eventData as Record<string, any>)?._typeId ??
              (eventData as Record<string, any>)?.fields?._typeId?.value ??
              '',
          ).trim()
        : ''
    const node =
      allNodes.find((n) => {
        if (n.data === eventData) return true
        const nk = n.key != null ? String(n.key).trim() : ''
        if (eventTreeKey !== '' && nk !== '' && nk === eventTreeKey) return true
        if (!n.data || typeof n.data !== 'object') return false
        const nodeId = String(
          (n.data as Record<string, any>)?._id ?? (n.data as Record<string, any>)?.fields?._id?.value ?? '',
        ).trim()
        const nodeTypeId = String(
          (n.data as Record<string, any>)?._typeId ?? (n.data as Record<string, any>)?.fields?._typeId?.value ?? '',
        ).trim()
        return eventId !== '' && nodeId === eventId && (eventTypeId === '' || nodeTypeId === eventTypeId)
      }) ??
      (selectedIdx >= 0 && selectedIdx < visibleNodes.length ? visibleNodes[selectedIdx] : null)

    if (!node || !node.data) {
      return
    }

    const payload = {
      node,
      target: event.target,
      column: event.column,
      selectedIdx: selectedIdx >= 0 ? selectedIdx : resolveNodeIndex(node),
      preview: event.preview === true,
    }

    // Vue emit не ждёт async parent listener — явно await beforeContextMenuShow.
    if (typeof props.beforeContextMenuShow === 'function') {
      const result = await props.beforeContextMenuShow(payload)
      if (Array.isArray(result)) {
        if (event.preview !== true) {
          contextMenuItemsAfterShow.value = result
        }
        rememberTreeRowContextOverride(node, result)
        return result
      }
    } else if (event.preview !== true) {
      emit('contextmenu', payload)
    }
  }
})

const getTreeRowContextCacheKey = (node: TreeNode): string => {
  const key = node?.key != null ? String(node.key).trim() : ''
  return key || String(resolveNodeIndex(node))
}

const rowContextShowOverflow = (node: TreeNode): boolean => {
  const override = rowContextOverrideByKey.value[getTreeRowContextCacheKey(node)]
  if (override) return override.showOverflow
  return rowContextVisibility.value.showOverflow
}

const rowContextShowPrimary = (node: TreeNode): boolean => {
  const override = rowContextOverrideByKey.value[getTreeRowContextCacheKey(node)]
  if (override) return override.showPrimary
  return rowContextVisibility.value.showColumn
}

const rowContextPrimaryPresentation = (node: TreeNode) => {
  const override = rowContextOverrideByKey.value[getTreeRowContextCacheKey(node)]
  const first = platformContextMenuItems.value[0]
  const item = override
    ? { label: override.label ?? first?.label, icon: override.icon ?? first?.icon }
    : first
  return resolveTableRowPrimaryActionPresentation(item)
}

const rememberTreeRowContextOverride = (node: TreeNode, items: any[]) => {
  const key = getTreeRowContextCacheKey(node)
  const next = resolveTableRowContextOverride(items)
  if (tableRowContextOverridesEqual(rowContextOverrideByKey.value[key], next)) return
  rowContextOverrideByKey.value = {
    ...rowContextOverrideByKey.value,
    [key]: next,
  }
}

const rowContextPreviewGate = createTableRowContextPreviewGate()

const selectTreeRowForContextAction = (node: TreeNode, event?: MouseEvent) => {
  if (!isNodeSelective(node) && !isTreeTableGrowUpRootNode(node)) {
    return
  }
  clickedRowKey.value = node.key
  const target = event?.target as HTMLElement | undefined
  const tr = target?.closest('tr')
  if (tr && isNodeSelective(node)) {
    tableContainer.value?.querySelectorAll('tr.row-active').forEach((row) => {
      row.classList.remove('row-active')
    })
    tr.classList.add('row-active')
  }
  if (isNodeSelective(node)) {
    emit('update:selected', [node.data])
  }
}

const onRowContextPrimary = async (event: MouseEvent, node: TreeNode) => {
  const selectedIdx = resolveNodeIndex(node)
  selectTreeRowForContextAction(node, event)
  const items = await prepareContextMenu(event, node.data, undefined, { selectedIdx })
  if (items == null) return
  rememberTreeRowContextOverride(node, items)
  if (!items.length) return
  const first = items[0]
  if (typeof first?.command === 'function') {
    await first.command({
      originalEvent: event,
      data: node.data,
      node,
      selectedIdx,
    })
  }
}

const onRowContextOverflow = async (event: MouseEvent, node: TreeNode) => {
  const selectedIdx = resolveNodeIndex(node)
  selectTreeRowForContextAction(node, event)
  const items = await prepareContextMenu(event, node.data, undefined, { selectedIdx })
  if (items == null) return
  rememberTreeRowContextOverride(node, items)
  showPreparedContextMenu(event)
}

const onRowContextShown = (node: TreeNode) => {
  if (!props.previewInlineMenu) return
  if (isTreeTableGrowUpRootNode(node)) return
  const key = getTreeRowContextCacheKey(node)
  if (rowContextOverrideByKey.value[key]) return
  rowContextPreviewGate.enqueue(key, async () => {
    if (rowContextOverrideByKey.value[key]) return
    const selectedIdx = resolveNodeIndex(node)
    const items = await prepareContextMenu(undefined, node.data, undefined, {
      selectedIdx,
      preview: true,
    })
    if (items) rememberTreeRowContextOverride(node, items)
  })
}

// Decode HTML entities in text
function decodeHtmlEntities(text: string): string {
  if (typeof text !== 'string') return text

  // Create a temporary DOM element to decode HTML entities
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

// безопасное получение значения из узла
function getNodeValue(node: TreeNode, columnValue: string): string {
  if (!node?.data) return ''
  const value = node.data[columnValue]
  const result = typeof value === 'object' && value?.value !== undefined
    ? String(value.value)
    : value !== undefined
      ? String(value)
      : ''

  // Decode HTML entities
  return decodeHtmlEntities(result)
}

/** Ключ узла TreeTable: `item.key` или неперечислимое поле на `data` (как в Swing — по колонкам unique*). */
function getItemIdentity(item: any): string | undefined {
  const fromItemKey = normalizeIdentityPart(item?.key)
  if (fromItemKey !== '') return fromItemKey

  const data = item?.data && typeof item.data === 'object' ? item.data : item
  if (!data || typeof data !== 'object') return undefined

  const fromRow = normalizeIdentityPart((data as Record<string, unknown>)[TREE_TABLE_NODE_KEY_FIELD])
  return fromRow !== '' ? fromRow : undefined
}

function normalizeCellValueForSignature(value: unknown): unknown {
  if (value == null) return value
  if (typeof value !== 'object') return value
  if ('value' in (value as Record<string, unknown>)) {
    return normalizeCellValueForSignature((value as { value?: unknown }).value)
  }
  if ((value as { inlineAction?: unknown }).inlineAction) {
    const action = (value as { action?: Record<string, unknown>; title?: unknown; icon?: unknown }).action
    return {
      inlineAction: true,
      title: (value as { title?: unknown }).title,
      icon: (value as { icon?: unknown }).icon,
      actionId: (action?._attributes as { id?: unknown } | undefined)?.id,
    }
  }
  return String(value)
}

function buildTreeItemsRenderSignature(items: any[], columnIds: string[]): string {
  const walk = (list: any[]): unknown[] =>
    list.map((item, index) => {
      const data = item?.data && typeof item.data === 'object' ? item.data : item
      const row = data && typeof data === 'object' ? data as Record<string, unknown> : {}
      const values = columnIds.map((id) => [id, normalizeCellValueForSignature(row[id])])
      return {
        key: getItemIdentity(item) ?? `${index}`,
        icon: item?.icon ?? row.icon,
        countChildren: row._countChildren ?? row.countChildren,
        isAllowChildren: item?.isAllowChildren,
        leaf: item?.leaf,
        values,
        children: Array.isArray(item?.children) ? walk(item.children) : undefined,
      }
    })

  return JSON.stringify({
    columns: columnIds,
    rows: Array.isArray(items) ? walk(items) : [],
  })
}

function parseTreeNodeDataChildCount(d: unknown): number | null {
  if (!d || typeof d !== 'object') return null
  const rec = d as Record<string, unknown>
  const cc = rec._countChildren ?? rec.countChildren
  if (cc != null && typeof cc === 'object' && 'value' in cc) {
    const n = Number((cc as { value: unknown }).value)
    return Number.isFinite(n) ? n : null
  }
  if (cc != null) {
    const n = Number(cc)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function treeNodeShowsExpandToggle(item: any, loadedChildren: TreeNode[] | undefined): boolean {
  if (loadedChildren && loadedChildren.length > 0) return true
  const d = item?.data
  const cc = parseTreeNodeDataChildCount(d)
  if (cc !== null && cc > 0) return true

  const allow =
    item?.isAllowChildren === true ||
    item?.isAllowChildren === 'true' ||
    d?.isAllowChildren === true ||
    d?.isAllowChildren === 'true'
  if (allow) {
    // Явный 0 детей после reload/merge — не показывать chevron (узел без потомков).
    if (cc === 0) return false
    // _countChildren ещё не было (lazy до expand) — оставить toggler.
    return true
  }
  return false
}

// Преобразование items в формат TreeTable
function transformItems(
  items: any[],
  pathPrefix: string = '',
  columnIds: string[] = [],
  depth = 0,
): TreeNode[] {
  if (!items || !Array.isArray(items)) return []

  return items.map((item, index) => {
    const identityKey = getItemIdentity(item)
    let key =
      identityKey != null && String(identityKey).trim() !== '' ? String(identityKey).trim() : ''
    if (key === '') {
      key = `${pathPrefix || 'mt'}|${index}`
      if (import.meta.env.DEV) {
        console.warn('[MTreeTable] Missing node key', { index, pathPrefix })
      }
    }

    const flatRow = item.data && typeof item.data === 'object' ? item.data : item
    const rowPresentation = buildTreeTableRowSwingPresentation(flatRow, columnIds)

    const node: TreeNode = {
      key,
      label: item.label || item.data?.subject || item.data?.name || item.data?.description || 'Без названия',
      data: item.data || item,
      children: item.children ? transformItems(item.children, key, columnIds, depth + 1) : undefined,
      icon: item.icon || rowPresentation.iconFileName || item.data?.icon,
      rowPresentation,
    }

    if (item.isAllowChildren !== undefined) {
      (node as any).isAllowChildren = item.isAllowChildren
    }
    if (item.expandByDefault !== undefined) {
      (node as any).expandByDefault = item.expandByDefault
    }
    const hideCheckbox = item[IKey.TT_HIDE_CHECKBOX] ?? item.hideCheckbox ?? flatRow?.[IKey.TT_HIDE_CHECKBOX]
    if (hideCheckbox !== undefined) {
      (node as any)[IKey.TT_HIDE_CHECKBOX] = hideCheckbox
      ;(node as any).hideCheckbox = hideCheckbox
    }
    const forbid = item[IKey.TT_FORBID_TO_SELECT_NODE] ?? flatRow?.[IKey.TT_FORBID_TO_SELECT_NODE]
    if (forbid !== undefined) {
      (node as any)[IKey.TT_FORBID_TO_SELECT_NODE] = forbid
    }
    const dropTarget = item[IKey.TT_IS_DROP_TARGET] ?? flatRow?.[IKey.TT_IS_DROP_TARGET]
    if (dropTarget !== undefined) {
      (node as any)[IKey.TT_IS_DROP_TARGET] = dropTarget
    }
    ;(node as any).treeDepth = depth
    ;(node as any).selectable = isTreeTableSelectiveNode(node, selectiveFlags.value) &&
      !isTreeTableGrowUpRootNode({ key, isGrowUpRoot: item.isGrowUpRoot })

    if (item.leaf === true) {
      ;(node as any).leaf = true
    } else if (treeNodeShowsExpandToggle(item, node.children)) {
      ;(node as any).leaf = false
    } else {
      ;(node as any).leaf = true
    }

    return node
  })
}

function wrapGrowUpRoot(mapped: TreeNode[]): TreeNode[] {
  if (!props.showRootNode) return mapped
  const root: TreeNode = {
    key: TREE_TABLE_GROW_UP_ROOT_KEY,
    label: props.descRoot || '',
    data: { isAllowChildren: true },
    children: mapped,
  }
  ;(root as any).isGrowUpRoot = true
  ;(root as any).isAllowChildren = true
  ;(root as any).leaf = false
  ;(root as any).treeDepth = 0
  ;(root as any).selectable = false
  return [root]
}

function expandAll(): void {
  const keys: Record<string, boolean> = {}
  const stack: TreeNode[] = [...nodes.value]
  while (stack.length > 0) {
    const node = stack.pop()
    if (!node) continue

    // Only expand if node has children or isAllowChildren is true
    const hasChildren = node.children && node.children.length > 0
    const isAllowChildren = (node as any).isAllowChildren !== false

    if (hasChildren || isAllowChildren) {
      // Check if node has expandByDefault flag
      const shouldExpand = props.expandByDefault || (node as any).expandByDefault === true
      if (shouldExpand && node.key) {
        keys[node.key] = true
      }
    }

    if (node.children) stack.push(...node.children)
  }
  expandedKeys.value = keys
}

/** Раскрыть все узлы с дочерними элементами */
function expandEntireTree(): void {
  const keys: Record<string, boolean> = { ...expandedKeys.value }
  const walk = (list: TreeNode[]) => {
    for (const node of list) {
      if (node.children?.length && node.key) {
        keys[node.key] = true
        walk(node.children)
      }
    }
  }
  walk(nodes.value)
  expandedKeys.value = keys
}

function findTreeNodeByKeyInList(list: TreeNode[], key: string): TreeNode | null {
  const k = String(key).trim()
  if (k === '') return null
  for (const n of list) {
    const nk = n.key != null ? String(n.key).trim() : ''
    if (nk === k) return n
    if (n.children?.length) {
      const found = findTreeNodeByKeyInList(n.children, k)
      if (found) return found
    }
  }
  return null
}

watch(
  [() => props.items, () => props.headers],
  ([newItems, hdrs]) => {
    const colIds = (hdrs || []).map((h: any) => String(h?.value ?? '')).filter(Boolean)
    const prevExpanded = {...expandedKeys.value}
    if (newItems && Array.isArray(newItems) && newItems.length > 0) {
      const nextSignature = buildTreeItemsRenderSignature(newItems, colIds)
      if (nextSignature === lastRenderedItemsSignature) {
        return
      }
      lastRenderedItemsSignature = nextSignature
      nodes.value = wrapGrowUpRoot(
        transformItems(newItems, '', colIds, props.showRootNode ? 1 : 0),
      )
      if (props.expandByDefault) {
        pendingLazyExpandKey.value = null
        expandAll()
        return
      }
      const nextExpanded: Record<string, boolean> = {}
      const pend = pendingLazyExpandKey.value
      const pendTrim = pend != null ? String(pend).trim() : ''
      const walk = (list: TreeNode[]) => {
        for (const n of list) {
          const nk = n.key != null ? String(n.key) : ''
          if (!nk || !prevExpanded[nk]) {
            if (n.children?.length) walk(n.children)
            continue
          }
          const hasChildren = !!(n.children && n.children.length > 0)
          const pendingLoad = pendTrim !== '' && nk === pendTrim
          if (hasChildren || pendingLoad) {
            nextExpanded[nk] = true
          }
          if (n.children?.length) walk(n.children)
        }
      }
      walk(nodes.value)
      if (props.showRootNode) {
        nextExpanded[TREE_TABLE_GROW_UP_ROOT_KEY] = true
      }
      if (props.showFormStateOptions && props.savedExpandedKeys) {
        Object.assign(nextExpanded, props.savedExpandedKeys)
        if (props.showRootNode) nextExpanded[TREE_TABLE_GROW_UP_ROOT_KEY] = true
      }
      if (pend) {
        const hit = findTreeNodeByKeyInList(nodes.value, pendTrim)
        if (hit?.children?.length) {
          nextExpanded[pendTrim] = true
          pendingLazyExpandKey.value = null
        }
      }
      expandedKeys.value = nextExpanded
      void nextTick(() => {
        expandedKeys.value = {...nextExpanded}
        const hl = searchHighlightKey.value
        if (hl) {
          expandToShowKey(hl)
          void nextTick(() => {
            highlightRowInDom(hl, { stealFocus: false })
          })
        }
      })
    } else {
      pendingLazyExpandKey.value = null
      lastRenderedItemsSignature = ''
      nodes.value = []
      expandedKeys.value = {}
    }
  },
  { immediate: true },
)

watch(
  expandedKeys,
  (val) => {
    if (!props.showFormStateOptions) return
    emit('saveSetting', { paramName: 'expandedKeys', paramValue: { ...val } })
  },
  { deep: true },
)

// Collect selected nodes' data from tree by selectedKeys
function getSelectedNodesData(nodeList: TreeNode[], keys: Record<string, boolean>): any[] {
  const result: any[] = []
  const walk = (list: TreeNode[]) => {
    for (const node of list) {
      if (isTreeTableGrowUpRootNode(node)) {
        if (node.children?.length) walk(node.children)
        continue
      }
      if (keys[node.key] && isNodeSelective(node)) {
        result.push(node.data)
      }
      if (node.children?.length) {
        walk(node.children)
      }
    }
  }
  walk(nodeList)
  return result
}

watch(
  selectedKeys,
  (newVal, oldVal) => {
    if (oldVal && JSON.stringify(newVal) === JSON.stringify(oldVal)) return

    let keys: Record<string, boolean> = { ...(newVal || {}) }
    if (props.withCheckbox) {
      const applied = applyTreeCheckingModeSelection({
        mode: normalizeTreeCheckingMode(props.checkingMode),
        prev: oldVal || {},
        next: newVal || {},
        tree: nodes.value,
        isSelective: (key) => {
          const n = findNodeByKey(nodes.value, key)
          return n ? isNodeSelective(n) : false
        },
      })
      if (JSON.stringify(applied) !== JSON.stringify(keys)) {
        selectedKeys.value = applied
        return
      }
      keys = applied
    } else {
      for (const key of Object.keys(keys)) {
        const n = findNodeByKey(nodes.value, key)
        if (!n || !isNodeSelective(n)) delete keys[key]
      }
      if (JSON.stringify(keys) !== JSON.stringify(newVal || {})) {
        selectedKeys.value = keys
        return
      }
    }

    const selectedData = getSelectedNodesData(nodes.value, keys)
    emit('update:value', { selectionKeys: { ...keys } })
    emit('update:selected', selectedData)
  },
  { deep: true }
)

function flattenNodes(nodeList: TreeNode[]): TreeNode[] {
  const result: TreeNode[] = []
  const visit = (list: TreeNode[]) => {
    for (const node of list) {
      result.push(node)
      if (node.children?.length) {
        visit(node.children)
      }
    }
  }
  visit(nodeList)
  return result
}

function flattenVisibleNodes(nodeList: TreeNode[]): TreeNode[] {
  const result: TreeNode[] = []
  const visit = (list: TreeNode[]) => {
    for (const node of list) {
      result.push(node)
      if (node.children?.length && expandedKeys.value[node.key]) {
        visit(node.children)
      }
    }
  }
  visit(nodeList)
  return result
}

/** Видимые узлы с родителем */
function flattenVisibleNodesWithParent(nodeList: TreeNode[], parentKey: string | null): FlatVisibleTreeRow[] {
  const result: FlatVisibleTreeRow[] = []
  const visit = (list: TreeNode[], pk: string | null) => {
    for (const node of list) {
      const k = node.key != null ? String(node.key) : ''
      if (isTreeTableGrowUpRootNode(node)) {
        if (node.children?.length && expandedKeys.value[k]) {
          visit(node.children, null)
        }
        continue
      }
      result.push({ key: k, parentKey: pk, data: node.data })
      if (node.children?.length && expandedKeys.value[k]) {
        visit(node.children, k)
      }
    }
  }
  visit(nodeList, parentKey)
  return result
}

function normalizeIdentityPart(value: unknown): string {
  return canonicalTreeNodeId(value)
}

function normalizeTreeRowKey(value: unknown): string {
  return value != null ? String(value).trim() : ''
}

function treeRowKeysEqual(a: unknown, b: unknown): boolean {
  const ak = normalizeTreeRowKey(a)
  const bk = normalizeTreeRowKey(b)
  return ak !== '' && ak === bk
}

function resolveNodeIndex(targetNode: TreeNode | null | undefined): number {
  if (!targetNode) return -1

  const flatNodes = flattenVisibleNodes(nodes.value)
  const targetKey = targetNode.key != null ? String(targetNode.key).trim() : ''
  const idx = flatNodes.findIndex((node) => {
    const nodeKey = node.key != null ? String(node.key).trim() : ''
    return (
      node === targetNode ||
      (targetKey !== '' && nodeKey !== '' && nodeKey === targetKey) ||
      (targetNode.data != null && node.data === targetNode.data)
    )
  })

  return idx >= 0 ? idx : -1
}

const onContextMenu = (node: TreeNode, column: string, event: Event) => {
  cancelPendingClick()
  // Use the shared context menu handler
  handleTableContextMenu(event, node.data, column, { selectedIdx: resolveNodeIndex(node) })
}

const onTreeTableContextMenu = (event: any) => {
  cancelPendingClick()
  const originalEvent: MouseEvent | undefined = event?.originalEvent || event
  const target = originalEvent?.target as HTMLElement | null

  if (!originalEvent) {
    return
  }
  let node: TreeNode | null = event?.node ?? null
  if (!node) {
    const rowKey = target?.closest('[data-row-key]')?.getAttribute('data-row-key')?.trim() ?? ''
    if (rowKey) {
      node = findNodeByKey(nodes.value, rowKey)
    }
  }
  if (!node) {
    const rowElement = target?.closest('tr')
    const rowDataKey =
      rowElement?.querySelector?.('[data-row-key]')?.getAttribute('data-row-key')?.trim() ?? ''
    if (rowDataKey) {
      node = findNodeByKey(nodes.value, rowDataKey)
    }
  }
  if (!node) {
    const rowElement = target?.closest('tr')
    const rowKey =
      rowElement?.getAttribute('data-pc-section') ||
      rowElement?.getAttribute('data-key') ||
      ''
    if (rowKey) {
      node = findNodeByKey(nodes.value, rowKey)
    }
  }
  if (!node) {
    const rowElement = target?.closest('tr') as HTMLTableRowElement | null
    const visibleRows = tableContainer.value?.querySelectorAll('.p-treetable-tbody > tr') || []
    const rowIdx = rowElement ? Array.from(visibleRows).indexOf(rowElement) : -1
    const flatVisible = flattenVisibleNodes(nodes.value)
    if (rowIdx >= 0 && rowIdx < flatVisible.length) {
      node = flatVisible[rowIdx]
    }
  }

  if (node) {
    // Find which column was clicked
    const cell = target?.closest('td')
    const rawIndex = cell ? Array.from(cell.parentElement?.children || []).indexOf(cell) : -1
    const headerIndex = rawIndex > 0 ? rawIndex - 1 : -1 // skip expander column
    const column = headerIndex >= 0 && headers.value[headerIndex]
      ? headers.value[headerIndex].value
      : ''

    handleTableContextMenu(originalEvent, node.data, column, { selectedIdx: resolveNodeIndex(node) })
  }
}

// Helper to recursively find a node by key
const findNodeByKey = (nodeList: TreeNode[], key: string): TreeNode | null => {
  const k = String(key).trim()
  for (const node of nodeList) {
    const nk = node.key != null ? String(node.key).trim() : ''
    if (nk === k) {
      return node
    }
    if (node.children) {
      const found = findNodeByKey(node.children, key)
      if (found) return found
    }
  }
  return null
}

function cloneRowDataForDrop(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') return {}
  try {
    return JSON.parse(JSON.stringify(data)) as Record<string, unknown>
  } catch {
    return {...(data as Record<string, unknown>) }
  }
}

function buildParentKeyMap(
  list: TreeNode[],
  parentKey: string | null = null,
  acc = new Map<string, string | null>(),
): Map<string, string | null> {
  for (const n of list) {
    const k = n.key != null ? String(n.key).trim() : ''
    if (k) acc.set(k, parentKey)
    if (n.children?.length) buildParentKeyMap(n.children, k, acc)
  }
  return acc
}

function isSourceStrictAncestorOfDest(
  sourceKey: string,
  destKey: string,
  parentMap: Map<string, string | null>,
): boolean {
  let cur = parentMap.get(destKey) ?? null
  while (cur != null) {
    if (cur === sourceKey) return true
    cur = parentMap.get(cur) ?? null
  }
  return false
}

function buildDropPayload(
  source: TreeNode,
  dest: TreeNode,
  sourceIdx: number,
  destIdx: number,
): Record<string, unknown> {
  const sourceData = cloneRowDataForDrop(source.data)
  const destData = cloneRowDataForDrop(dest.data)
  sourceData.rowNum = sourceIdx
  destData.rowNum = destIdx
  return { source: sourceData, dest: destData }
}

function validateTreeDrop(sourceKey: string, destNode: TreeNode | null): destNode is TreeNode {
  if (!destNode) return false
  const dk = destNode.key != null ? String(destNode.key).trim() : ''
  if (!dk || sourceKey === dk) return false
  const parentMap = buildParentKeyMap(nodes.value)
  if (isSourceStrictAncestorOfDest(sourceKey, dk, parentMap)) return false
  return isTreeTableDropTarget(destNode)
}

/**
 * Строка под курсором. PrimeVue кладёт expander/checkbox рядом с телом ячейки — иногда target не внутри [data-row-key];
 */
function findDestNodeFromDragTarget(e: DragEvent): TreeNode | null {
  const root = tableContainer.value
  if (!root) return null

  const resolveFromStart = (start: HTMLElement | null): TreeNode | null => {
    if (!start || !root.contains(start)) return null

    const row =
      start.closest?.('tr') ||
      (start.closest?.('[role="row"]') as HTMLElement | null) ||
      null
    if (!row || !root.contains(row) || row.closest('thead')) return null

    let cur: HTMLElement | null = start
    while (cur && row.contains(cur)) {
      const dk = cur.getAttribute('data-row-key')?.trim()
      if (dk) return findNodeByKey(nodes.value, dk)
      cur = cur.parentElement
    }

    const rowKeyEl = row.querySelector('[data-row-key]') as HTMLElement | null
    const rowKey = rowKeyEl?.getAttribute('data-row-key')?.trim()
    if (rowKey) return findNodeByKey(nodes.value, rowKey)
    return null
  }

  const startEl =
    e.target instanceof HTMLElement ? e.target : (e.target as Node | null)?.parentElement ?? null
  const fromTarget = resolveFromStart(startEl)
  if (fromTarget) return fromTarget

  const cx = e.clientX
  const cy = e.clientY
  if (Number.isFinite(cx) && Number.isFinite(cy)) {
    for (const el of document.elementsFromPoint(cx, cy)) {
      if (!(el instanceof HTMLElement)) continue
      if (!root.contains(el)) continue
      const hit = resolveFromStart(el)
      if (hit) return hit
    }
  }
  return null
}

const onTableDragEnterCapture = (e: DragEvent) => {
  if (!canDrag.value || !internalTreeDragActive.value) return
  if (!e.dataTransfer || !activeDragSourceKey.value) return
  e.preventDefault()
}

/**
 * capture: dragover должен вызывать preventDefault на зоне таблицы, иначе курсор «запрещено»
 * при наведении на tr/td/колонку-expander.
 */
const onTableDragOverCapture = (e: DragEvent) => {
  if (!canDrag.value || !internalTreeDragActive.value) return
  const dt = e.dataTransfer
  if (!dt) return
  const sk = activeDragSourceKey.value
  if (!sk) return

  // Внутренний DnD: сначала preventDefault.
  e.preventDefault()

  const destNode = findDestNodeFromDragTarget(e)
  if (!validateTreeDrop(sk, destNode)) {
    dt.dropEffect = 'none'
    return
  }
  dt.dropEffect = 'move'
}

const onTableDropCapture = (e: DragEvent) => {
  if (!canDrag.value || !internalTreeDragActive.value) return
  const dt = e.dataTransfer
  if (!dt) return

  e.preventDefault()
  e.stopPropagation()

  const destNode = findDestNodeFromDragTarget(e)
  const rawKey =
    dt.getData(DND_KEY_MIME) ||
    dt.getData('text/plain') ||
    activeDragSourceKey.value ||
    ''
  const srcKey = String(rawKey).trim()

  internalTreeDragActive.value = false
  activeDragSourceKey.value = null

  if (!srcKey || !validateTreeDrop(srcKey, destNode)) return

  const sourceNode = findNodeByKey(nodes.value, srcKey)
  if (!sourceNode) return
  const srcIdx = resolveNodeIndex(sourceNode)
  const dstIdx = resolveNodeIndex(destNode)
  if (srcIdx < 0 || dstIdx < 0) return
  emit('node-drop', buildDropPayload(sourceNode, destNode, srcIdx, dstIdx))
}

const onCellDragStart = (e: DragEvent, node: TreeNode) => {
  if (!canDrag.value) return
  e.stopPropagation()
  const key = node.key != null ? String(node.key).trim() : ''
  if (!key || !e.dataTransfer) return
  activeDragSourceKey.value = key
  internalTreeDragActive.value = true
  try {
    e.dataTransfer.setData(DND_KEY_MIME, key)
    e.dataTransfer.setData('text/plain', key)
    e.dataTransfer.effectAllowed = 'move'
  } catch {
    /* ignore */
  }
}

const onCellDragEnd = () => {
  activeDragSourceKey.value = null
  internalTreeDragActive.value = false
}

let clickTimer: ReturnType<typeof setTimeout> | null = null
const DOUBLE_CLICK_DELAY = 250 // ms
const cancelPendingClick = () => {
  if (clickTimer) {
    clearTimeout(clickTimer)
    clickTimer = null
  }
}

const onCellClick = (node: TreeNode, column: string, event?: MouseEvent) => {
  const applyActiveRow = (tr: HTMLElement | null) => {
    if (!tr) return
    tableContainer.value?.querySelectorAll('tr.row-active').forEach(row => {
      row.classList.remove('row-active')
    })
    tr.classList.add('row-active')
  }

  if (isTreeTableGrowUpRootNode(node)) {
    clickedRowKey.value = node.key
    if (event) applyActiveRow((event.target as HTMLElement).closest('tr'))
    cancelPendingClick()
    clickTimer = setTimeout(() => {
      clickTimer = null
      emit('click', { node, column, data: node.data, selectedIdx: resolveNodeIndex(node) })
    }, DOUBLE_CLICK_DELAY)
    return
  }

  if (!isNodeSelective(node)) {
    cancelPendingClick()
    clickTimer = setTimeout(() => {
      clickTimer = null
      emit('click', { node, column, data: node.data, selectedIdx: resolveNodeIndex(node) })
    }, DOUBLE_CLICK_DELAY)
    return
  }

  const multi = !props.withCheckbox && props.isMultiSelectEnabled
  if (multi && event) {
    if (event.ctrlKey || event.metaKey) {
      const next = { ...selectedKeys.value }
      if (next[node.key]) delete next[node.key]
      else next[node.key] = true
      selectedKeys.value = next
      clickedRowKey.value = node.key
      if (event) applyActiveRow((event.target as HTMLElement).closest('tr'))
      cancelPendingClick()
      clickTimer = setTimeout(() => {
        clickTimer = null
        emit('click', { node, column, data: node.data, selectedIdx: resolveNodeIndex(node) })
        emit('update:selected', getSelectedNodesData(nodes.value, next))
      }, DOUBLE_CLICK_DELAY)
      return
    }
    if (event.shiftKey && clickedRowKey.value) {
      const vis = flattenVisibleNodes(nodes.value).filter((n) => isNodeSelective(n))
      const a = vis.findIndex((n) => treeRowKeysEqual(n.key, clickedRowKey.value))
      const b = vis.findIndex((n) => treeRowKeysEqual(n.key, node.key))
      if (a >= 0 && b >= 0) {
        const lo = Math.min(a, b)
        const hi = Math.max(a, b)
        const next: Record<string, boolean> = {}
        for (let i = lo; i <= hi; i++) next[vis[i].key] = true
        selectedKeys.value = next
        if (event) applyActiveRow((event.target as HTMLElement).closest('tr'))
        cancelPendingClick()
        clickTimer = setTimeout(() => {
          clickTimer = null
          emit('click', { node, column, data: node.data, selectedIdx: resolveNodeIndex(node) })
          emit('update:selected', getSelectedNodesData(nodes.value, next))
        }, DOUBLE_CLICK_DELAY)
        return
      }
    }
  }

  clickedRowKey.value = node.key
  if (event) applyActiveRow((event.target as HTMLElement).closest('tr'))

  cancelPendingClick()

  clickTimer = setTimeout(() => {
    clickTimer = null
    const selectedIdx = resolveNodeIndex(node)
    emit('click', { node, column, data: node.data, selectedIdx })
    if (!props.withCheckbox) {
      selectedKeys.value = { [node.key]: true }
    }
    emit('update:selected', [node.data])
  }, DOUBLE_CLICK_DELAY)
}

const onCellDoubleClick = (node: TreeNode, column: string) => {
  cancelPendingClick()

  if (isTreeTableGrowUpRootNode(node)) {
    emit('find-parent', { node })
    return
  }

  if (!isNodeSelective(node)) {
    return
  }

  const selectedIdx = resolveNodeIndex(node)
  emit('dblclick', { node, column, data: node.data, selectedIdx })
}

const isRowContextActionButtonTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest('.table-row-context-actions__btn'))

const onRowContextCellClick = (event: MouseEvent, node: TreeNode) => {
  if (isRowContextActionButtonTarget(event.target)) return
  onCellClick(node, '', event)
}

const onRowContextCellDblclick = (event: MouseEvent, node: TreeNode) => {
  if (isRowContextActionButtonTarget(event.target)) return
  onCellDoubleClick(node, '')
}

const onNodeSelect = () => {
  // Checkbox selection: selectedKeys already updated by TreeTable; watch will emit update:selected
}

/**
 * Ленивая загрузка детей: родитель вызывает reload с table_params / __rowNum.
 */
const onNodeExpand = (node: TreeNode) => {
  if (!node) return
  if (node.children && node.children.length > 0) return
  const allow = treeNodeShowsExpandToggle(
    { data: node.data, isAllowChildren: (node as any).isAllowChildren },
    node.children,
  )
  if (!allow) return
  const rowNum = resolveNodeIndex(node)
  if (rowNum < 0) return
  const k = node.key != null ? String(node.key) : ''
  if (k) pendingLazyExpandKey.value = k
  const expandedSnapshot = {...expandedKeys.value}
  if (k) expandedSnapshot[k] = true
  emit('load-children', { node, rowNum, expandedSnapshot })
}

function findPathKeysToTarget(list: TreeNode[], targetKey: string): string[] | null {
  const tk = String(targetKey).trim()
  if (!tk) return null
  for (const n of list) {
    const nk = n.key != null ? String(n.key).trim() : ''
    if (!nk) continue
    if (nk === tk) {
      return [nk]
    }
    if (n.children?.length) {
      const sub = findPathKeysToTarget(n.children, targetKey)
      if (sub?.length) {
        return [nk, ...sub]
      }
    }
  }
  return null
}

function expandToShowKey(targetKey: string): boolean {
  const tk = String(targetKey).trim()
  if (!tk) return false
  const path = findPathKeysToTarget(nodes.value, tk)
  if (!path?.length) return false
  const next = { ...expandedKeys.value }
  for (let i = 0; i < path.length - 1; i++) {
    next[path[i]] = true
  }
  expandedKeys.value = next
  void nextTick(() => {
    expandedKeys.value = { ...next }
  })
  return true
}

function scrollRowIntoView(targetKey: string): void {
  const tk = normalizeTreeRowKey(targetKey)
  if (!tk || !tableContainer.value) return
  const rowEl = findRowElementByKey(tk)
  const tr = (rowEl?.closest('tr') ?? rowEl) as HTMLElement | null
  if (!tr) return
  const scrollBody = tableContainer.value.querySelector(
    '.p-treetable-scrollable-body',
  ) as HTMLElement | null
  if (scrollBody) {
    const rowTop = tr.offsetTop
    const rowBottom = rowTop + tr.offsetHeight
    const viewTop = scrollBody.scrollTop
    const viewBottom = viewTop + scrollBody.clientHeight
    if (rowTop < viewTop || rowBottom > viewBottom) {
      scrollBody.scrollTop = Math.max(0, rowTop - Math.floor(scrollBody.clientHeight / 3))
    }
    return
  }
  tr.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
}

function focusTreeTable(): void {
  tableContainer.value?.focus({ preventScroll: true })
}

function applyFocusAndSelectKeyState(targetKey: string): void {
  const tk = normalizeTreeRowKey(targetKey)
  if (!tk) return
  const node = findTreeNodeByKeyInList(nodes.value, tk)
  clickedRowKey.value = tk
  if (node && !isNodeSelective(node)) {
    searchHighlightKey.value = tk
    return
  }
  if (props.withCheckbox) {
    selectedKeys.value = { ...selectedKeys.value, [tk]: true }
  } else {
    selectedKeys.value = { [tk]: true }
  }
  searchHighlightKey.value = tk
  if (searchHighlightTimer) {
    clearTimeout(searchHighlightTimer)
  }
  searchHighlightTimer = setTimeout(() => {
    if (searchHighlightKey.value === tk && selectedKeys.value[tk]) {
      searchHighlightTimer = null
      return
    }
    if (searchHighlightKey.value === tk) {
      searchHighlightKey.value = null
    }
    searchHighlightTimer = null
  }, 2500)
}

function findRowElementByKey(targetKey: string): HTMLElement | null {
  const tk = normalizeTreeRowKey(targetKey)
  if (!tk || !tableContainer.value) return null
  const escaped = CSS.escape(tk)
  const rowEl = tableContainer.value.querySelector(
    `[data-row-key="${escaped}"]`,
  ) as HTMLElement | null
  if (rowEl) return rowEl
  for (const el of tableContainer.value.querySelectorAll('[data-row-key]')) {
    const dk = (el as HTMLElement).getAttribute('data-row-key')?.trim() ?? ''
    if (dk === tk) return el as HTMLElement
  }
  const node = findTreeNodeByKeyInList(nodes.value, tk)
  if (node) {
    const idx = resolveNodeIndex(node)
    if (idx >= 0) {
      const tr = tableContainer.value.querySelectorAll('.p-treetable-tbody > tr')[idx] as
        | HTMLTableRowElement
        | undefined
      if (tr) {
        return (tr.querySelector('[data-row-key]') as HTMLElement | null) ?? tr
      }
    }
  }
  return null
}

function highlightRowInDom(
  targetKey: string,
  options?: { stealFocus?: boolean },
): boolean {
  const tk = normalizeTreeRowKey(targetKey)
  if (!tk || !tableContainer.value) return false
  scrollRowIntoView(tk)
  const rowEl = findRowElementByKey(tk)
  const tr = rowEl?.closest('tr') as HTMLTableRowElement | null
  if (!tr) return false
  tableContainer.value
    .querySelectorAll('.p-treetable-tbody tr.row-active')
    .forEach((el) => el.classList.remove('row-active'))
  tr.classList.add('row-active')
  if (options?.stealFocus !== false) {
    focusTreeTable()
  }
  return true
}

function focusAndSelectKey(targetKey: string): boolean {
  const tk = String(targetKey).trim()
  if (!tk) return false
  expandToShowKey(tk)
  applyFocusAndSelectKeyState(tk)
  void nextTick(() => {
    highlightRowInDom(tk)
  })
  return true
}

function hasNodeKey(targetKey: string): boolean {
  const tk = String(targetKey).trim()
  if (!tk) return false
  return findTreeNodeByKeyInList(nodes.value, tk) != null
}

/** Раскрыть путь и выделить строку после появления узла в DOM (после lazy expand / refill). */
async function focusAndSelectKeyWhenReady(
  targetKey: string,
  options?: { stealFocus?: boolean },
): Promise<boolean> {
  const tk = String(targetKey).trim()
  if (!tk) return false
  for (let attempt = 0; attempt < 48; attempt++) {
    expandToShowKey(tk)
    if (hasNodeKey(tk)) {
      applyFocusAndSelectKeyState(tk)
      await nextTick()
      await nextTick()
      if (highlightRowInDom(tk, options)) {
        return true
      }
    }
    await nextTick()
    if (attempt === 0) {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
    } else {
      await new Promise<void>((resolve) => setTimeout(resolve, 50))
    }
  }
  return false
}

function getFlattenVisibleRowsForReload(): FlatVisibleTreeRow[] {
  return flattenVisibleNodesWithParent(nodes.value, null)
}

function getCheckboxSelectionKeysSnapshot(): Record<string, unknown> {
  return { ...selectedKeys.value }
}

function ensureCheckboxSelectedForKey(targetKey: string, checked: boolean): void {
  if (!props.withCheckbox) return
  const tk = String(targetKey).trim()
  if (!tk) return
  const next = { ...selectedKeys.value }
  if (checked) {
    next[tk] = true
  } else {
    delete next[tk]
  }
  selectedKeys.value = next
}

function getExpandedSnapshot(): Record<string, boolean> {
  return {...expandedKeys.value}
}

/**
 * Сбрасывает визуальный фокус поиска: clickedRowKey, selectedKeys (single-mode)
 * и search-highlight. Нужен после notFound/wrap, чтобы строка прошлого
 * успешного результата не висела подсвеченной.
 */
function clearSearchFocus(): void {
  clickedRowKey.value = null
  searchHighlightKey.value = null
  if (searchHighlightTimer) {
    clearTimeout(searchHighlightTimer)
    searchHighlightTimer = null
  }
  if (!props.withCheckbox) {
    selectedKeys.value = {}
  }
}

defineExpose({
  expandEntireTree,
  expandAll,
  getFlattenVisibleRowsForReload,
  getExpandedSnapshot,
  getCheckboxSelectionKeysSnapshot,
  expandToShowKey,
  ensureCheckboxSelectedForKey,
  focusTreeTable,
  focusAndSelectKey,
  focusAndSelectKeyWhenReady,
  hasNodeKey,
  clearSearchFocus,
})

// Cleanup timer on unmount
onBeforeUnmount(() => {
  if (tableResizeObserver) {
    tableResizeObserver.disconnect()
    tableResizeObserver = null
  }
  if (clickTimer) {
    clearTimeout(clickTimer)
    clickTimer = null
  }
  if (searchHighlightTimer) {
    clearTimeout(searchHighlightTimer)
    searchHighlightTimer = null
  }
})

</script>

<style scoped lang="scss">
.modern-tree-table {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.modern-tree-table--hide-border {
  box-shadow: none;
  border: none;
  border-radius: 0;
}

.modern-tree-table--style-striped :deep(.p-treetable-tbody > tr:nth-child(even)) {
  background: #eef1f4 !important;
}

.modern-tree-table--fill,
.modern-tree-table--fixed-scroll {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;

  :deep(.p-treetable) {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  :deep(.p-treetable-wrapper),
  :deep(.p-treetable-table-container) {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    scrollbar-gutter: stable;
  }

  :deep(.p-treetable-thead) {
    position: sticky;
    top: 0;
    z-index: 1;
  }
}

.modern-tree-table--fill {
  height: 100%;
  min-height: calc(var(--platform-table-fill-min-body, 120px) + 2.75rem);
}

.modern-tree-table--fixed-scroll {
  flex: 0 1 auto;
}

:deep(.p-treetable) {
  background: #fff !important;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;

  .p-treetable-wrapper {
    background: #fff !important;
  }

  .p-treetable-thead {
    background: #f4f6f8 !important;

    th {
      background: #f4f6f8 !important;
      border: 1px solid #e5e7eb !important;
      color: #374151 !important;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 1rem;
    }
  }

  .p-treetable-tbody {
    background: #f4f6f8 !important;

    tr {
      cursor: pointer;
      transition: all 0.2s ease;
      background: #f4f6f8 !important;

      &:nth-child(odd),
      &:nth-child(even) {
        background: #f4f6f8 !important;
      }

      &:hover {
        background: #e8ecf0 !important;
      }

      &.p-highlight {
        background: #dde7f0 !important;

        &:hover {
          background: #d0dde8 !important;
        }
      }

      // Active row (clicked) - visual highlight independent of checkbox selection
      &.row-active {
        background: #e3f2fd !important;

        td {
          background: #e3f2fd !important;
        }

        &:hover {
          background: #bbdefb !important;

          td {
            background: #bbdefb !important;
          }
        }
      }

      td {
        border-color: #e5e7eb !important;
        padding: 1rem;
        color: #374151 !important;
        background: transparent !important;
      }
    }
  }

  .p-treetable-scrollable-body {
    background: #f4f6f8 !important;
  }

  .p-treetable-scrollable-header {
    background: #f4f6f8 !important;
  }

  .p-treetable-node-checkbox,
  .p-checkbox.p-treetable-node-checkbox {
    .p-checkbox-box {
      background: #fff !important;
      border: 1px solid #d1d5db !important;
      border-radius: 4px;

      &:hover {
        border-color: #9ca3af !important;
        background: #f9fafb !important;
      }
    }

    &[data-p-checked="true"] .p-checkbox-box {
      background: var(--primary-color, #1e3a6e) !important;
      border-color: var(--primary-color, #1e3a6e) !important;

      &:hover {
        background: color-mix(in srgb, var(--primary-color, #1e3a6e) 92%, black) !important;
        border-color: color-mix(in srgb, var(--primary-color, #1e3a6e) 92%, black) !important;
      }
    }

    &[data-p-indeterminate="true"] .p-checkbox-box {
      background: var(--primary-color, #1e3a6e) !important;
      border-color: var(--primary-color, #1e3a6e) !important;
    }

    .p-checkbox-icon {
      color: #fff !important;
    }

    .p-checkbox-input {
      &:focus-visible + .p-checkbox-box {
        outline: 2px solid var(--primary-color, #1e3a6e);
        outline-offset: 2px;
      }
    }
  }

  table {
    background: #f4f6f8 !important;
    border-collapse: collapse;

    thead {
      background: #f4f6f8 !important;

      tr {
        background: #f4f6f8 !important;

        th {
          background: #f4f6f8 !important;
          color: #374151 !important;
        }
      }
    }

    tbody {
      background: #f4f6f8 !important;

      tr {
        background: #f4f6f8 !important;

        td {
          background: transparent !important;
          color: #374151 !important;
        }

        &:hover {
          background: #e8ecf0 !important;

          td {
            background: #e8ecf0 !important;
          }
        }
      }
    }
  }
}

:deep(.p-treetable.tree-table-without-header .p-treetable-thead) {
  display: none;
}

.tree-table-cell {
  display: flex;
  align-items: center;
  min-height: 40px;
  height: 100%;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  cursor: pointer;
  padding: 0.5rem;
  margin: -0.5rem;
  box-sizing: content-box;

  &.row-search-match {
    background: #fff3cd;
    outline: 1px solid #ffc107;
    border-radius: 4px;
  }
}

/* Text/default: single line + ellipsis. Wrap only MultiLineText. */
:deep(.p-treetable-table > tbody > tr > td .cell-content),
:deep(.p-treetable-table > tbody > tr > td .cell-content > *) {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: normal;
}

:deep(td[data-type='MultiLineText'] .tree-table-cell),
:deep(td[data-type='MULTILINETEXT'] .tree-table-cell),
:deep(td[data-type='multilinetext'] .tree-table-cell) {
  align-items: flex-start;
}

:deep(td[data-type='MultiLineText'] .cell-content),
:deep(td[data-type='MULTILINETEXT'] .cell-content),
:deep(td[data-type='multilinetext'] .cell-content),
:deep(td[data-type='MultiLineText'] .cell-content > *),
:deep(td[data-type='MULTILINETEXT'] .cell-content > *),
:deep(td[data-type='multilinetext'] .cell-content > *) {
  overflow: visible;
  text-overflow: unset;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.header-content.inline-actions-header {
  justify-content: center;
  cursor: default;
}

.header-text {
  font-size: 0.875rem;
}

.empty-cell {
  color: #9ca3af;
  font-style: italic;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 1rem 1rem;
  color: #6b7280;

  .empty-icon {
    font-size: 1.5rem;
    opacity: 0.35;
  }

  .empty-text {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
  }
}

:deep(th.table-row-context-actions-col),
:deep(td.table-row-context-actions-col) {
  position: sticky;
  right: 0;
  box-sizing: border-box;
  overflow: visible;
}

:deep(.p-treetable-thead > tr > th.table-row-context-actions-col) {
  z-index: 3;
  background: #f4f6f8 !important;
  padding: 0.2rem 0.2rem !important;
  box-shadow: -4px 0 8px rgba(15, 23, 42, 0.06);
}

:deep(.p-treetable-tbody > tr > td.table-row-context-actions-col) {
  z-index: 1;
  background: #f4f6f8 !important;
  padding: 0.2rem 0.2rem !important;
  vertical-align: middle !important;
  box-shadow: -4px 0 8px rgba(15, 23, 42, 0.06);
}

.tree-table-row-context-hit {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  min-height: 1.5rem;
}

:deep(.p-treetable-tbody > tr:hover > td.table-row-context-actions-col) {
  background: #e8ecf0 !important;
}

:deep(.p-treetable-tbody > tr.p-highlight > td.table-row-context-actions-col) {
  background: #dde7f0 !important;
}

:deep(.p-treetable-tbody > tr.row-active > td.table-row-context-actions-col) {
  background: #e3f2fd !important;
}

:deep(.p-treetable-tbody > tr.row-active:hover > td.table-row-context-actions-col) {
  background: #bbdefb !important;
}

:deep(.p-treetable-tbody > tr:has([data-hide-checkbox='true']) .p-treetable-node-checkbox),
:deep(.p-treetable-tbody > tr:has([data-not-selective='true']) .p-treetable-node-checkbox) {
  visibility: hidden;
  pointer-events: none;
}

.modern-tree-table--skip-default-styles {
  :deep(.p-treetable) {
    border: none;
    background: transparent;
  }

  :deep(.p-treetable-thead > tr) {
    background: transparent;
    border: none;
  }

  :deep(.p-treetable-thead > tr > th) {
    background: transparent;
    border: none;
    padding: 0;
    color: inherit;
  }

  :deep(.p-treetable-tbody > tr) {
    background: transparent;
    border: none;
  }

  :deep(.p-treetable-tbody > tr > td) {
    background: transparent;
    border: none;
    padding: 0;
  }

  :deep(.p-treetable-tbody > tr:hover) {
    background: transparent !important;
  }

  :deep(.p-treetable-tbody > tr.p-highlight) {
    background: transparent !important;
  }

  :deep(.p-paginator) {
    background: transparent;
    border: none;
  }
}
</style>
